
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  BadgeDollarSign,
  Users,
  LineChart,
  ChevronDown,
  Wallet,
  PieChart,
  Handshake,
  Award,
  CheckCircle,
  Link as LinkIcon
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

interface StepProps {
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const Step: React.FC<StepProps> = ({ number, title, description, icon, color }) => (
  <motion.div
    variants={itemVariants}
    className="flex items-start gap-4"
  >
    <div className={`w-12 h-12 ${color} rounded-full flex items-center justify-center flex-shrink-0`}>
      {icon}
    </div>
    <div>
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-gray-700 font-medium text-sm">
          {number}
        </div>
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      <p className="text-gray-600 mt-1">{description}</p>
    </div>
  </motion.div>
);

export function AffiliateProgramSection() {
  return (
    <section id="affiliate" className="py-20 bg-gradient-to-br from-amber-50 to-purple-50/30">
      <div className="container mx-auto px-4">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
          className="text-center mb-12"
        >
          <div className="inline-block bg-amber-50 rounded-full px-4 py-1 mb-4 border border-amber-200">
            <span className="text-amber-600 font-medium text-sm flex items-center gap-1">
              <BadgeDollarSign className="h-4 w-4" /> Earn With Us
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Affiliate Program</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Share Edaptica with your audience and earn generous commissions for every subscription
          </p>
        </motion.div>
        
        {/* Tabbed content for better organization */}
        <Tabs defaultValue="overview" className="max-w-5xl mx-auto">
          <TabsList className="grid grid-cols-4 max-w-2xl mx-auto mb-8">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="process">Process</TabsTrigger>
            <TabsTrigger value="earnings">Earnings</TabsTrigger>
            <TabsTrigger value="faq">FAQ</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white p-6 rounded-lg shadow-md"
              >
                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-2">Why Become an Affiliate?</h3>
                  <p className="text-gray-600">
                    Join our thriving affiliate community and earn commissions by promoting a product
                    that genuinely helps students succeed.
                  </p>
                </div>
                
                <ul className="space-y-4">
                  {[
                    { icon: <BadgeDollarSign className="h-5 w-5 text-green-500" />, text: "Generous commission rates (up to ₹200 per signup)" },
                    { icon: <Users className="h-5 w-5 text-blue-500" />, text: "Access to a growing market of students" },
                    { icon: <LineChart className="h-5 w-5 text-purple-500" />, text: "Real-time tracking and performance analytics" },
                    { icon: <Wallet className="h-5 w-5 text-amber-500" />, text: "Weekly payouts via UPI or bank transfer" },
                    { icon: <Handshake className="h-5 w-5 text-rose-500" />, text: "30-day attribution window for referrals" },
                  ].map((item, i) => (
                    <motion.li key={i} variants={itemVariants} className="flex items-center gap-3">
                      {item.icon}
                      <span>{item.text}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
              
              <div>
                <motion.div 
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-amber-100/50 to-purple-100/50 p-6 rounded-lg shadow-md h-full"
                >
                  <h3 className="text-2xl font-bold mb-4">Who Can Apply?</h3>
                  
                  <div className="space-y-4">
                    <motion.div variants={itemVariants} className="flex gap-3">
                      <div className="bg-white p-3 rounded-full">
                        <Award className="h-5 w-5 text-amber-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Educators & Tutors</h4>
                        <p className="text-gray-600 text-sm">
                          Recommend complementary learning tools to your students
                        </p>
                      </div>
                    </motion.div>
                    
                    <motion.div variants={itemVariants} className="flex gap-3">
                      <div className="bg-white p-3 rounded-full">
                        <Users className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Content Creators</h4>
                        <p className="text-gray-600 text-sm">
                          Share with your audience on YouTube, Instagram, or your blog
                        </p>
                      </div>
                    </motion.div>
                    
                    <motion.div variants={itemVariants} className="flex gap-3">
                      <div className="bg-white p-3 rounded-full">
                        <LineChart className="h-5 w-5 text-purple-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Student Communities</h4>
                        <p className="text-gray-600 text-sm">
                          Recommend to your community or student group
                        </p>
                      </div>
                    </motion.div>
                    
                    <motion.div variants={itemVariants} className="flex gap-3">
                      <div className="bg-white p-3 rounded-full">
                        <PieChart className="h-5 w-5 text-green-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Education Websites</h4>
                        <p className="text-gray-600 text-sm">
                          Partner with us to offer complementary learning resources
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
            
            <div className="mt-8 flex justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white">
                <Link to="/affiliate/apply">Apply to Become an Affiliate</Link>
              </Button>
            </div>
          </TabsContent>
          
          {/* Process Tab */}
          <TabsContent value="process" className="space-y-8">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-2xl font-bold mb-6 text-center">How It Works</h3>
                
                <div className="space-y-6">
                  <Step 
                    number={1}
                    title="Apply to the Affiliate Program"
                    description="Fill out our simple application form, telling us about yourself and how you plan to promote Edaptica."
                    icon={<Users className="h-6 w-6 text-white" />}
                    color="bg-blue-500"
                  />
                  
                  <Step 
                    number={2}
                    title="Get Approved"
                    description="Our team reviews your application. Once approved, you'll receive your unique referral code."
                    icon={<CheckCircle className="h-6 w-6 text-white" />}
                    color="bg-green-500"
                  />
                  
                  <Step 
                    number={3}
                    title="Share Your Referral Link"
                    description="Promote Edaptica through your personal referral link on your website, social media, or with your audience."
                    icon={<LinkIcon className="h-6 w-6 text-white" />}
                    color="bg-purple-500"
                  />
                  
                  <Step 
                    number={4}
                    title="Earn Commissions"
                    description="Earn ₹15-₹200 for every user who subscribes through your link. Commissions are based on the subscription plan."
                    icon={<BadgeDollarSign className="h-6 w-6 text-white" />}
                    color="bg-amber-500"
                  />
                </div>
              </div>
              
              <div className="text-center">
                <Button asChild variant="outline" className="hover-border-glow">
                  <Link to="/affiliate/terms">View Complete Program Details</Link>
                </Button>
              </div>
            </motion.div>
          </TabsContent>
          
          {/* Earnings Tab */}
          <TabsContent value="earnings" className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-3">
                <motion.div
                  variants={containerVariants}
                  initial="hidden" 
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-lg shadow-md h-full"
                >
                  <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-amber-600 to-rose-600 bg-clip-text text-transparent">
                    How Affiliate Commissions Work
                  </h3>
                  <p className="mb-4 text-gray-700">
                    By joining the Edaptica Affiliate Program, you can earn commissions every time a user signs up through your referral link and purchases any of our subscription plans.
                  </p>
                  
                  <div className="overflow-x-auto mt-4">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-muted/50">
                          <th className="border p-2 text-left">Plan Type</th>
                          <th className="border p-2 text-left">Price Paid by User</th>
                          <th className="border p-2 text-left">Your Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border p-2">7-Day Trial</td>
                          <td className="border p-2">₹12</td>
                          <td className="border p-2">₹5 <span className="text-xs text-muted-foreground">(high-volume only)</span></td>
                        </tr>
                        <tr>
                          <td className="border p-2">Basic Monthly</td>
                          <td className="border p-2">₹59/month</td>
                          <td className="border p-2">₹15 per paid user</td>
                        </tr>
                        <tr>
                          <td className="border p-2">Pro Monthly</td>
                          <td className="border p-2">₹118/month</td>
                          <td className="border p-2">₹25 per paid user</td>
                        </tr>
                        <tr>
                          <td className="border p-2">Basic Annual</td>
                          <td className="border p-2">₹590/year</td>
                          <td className="border p-2">₹100 per paid user</td>
                        </tr>
                        <tr>
                          <td className="border p-2">Pro Annual</td>
                          <td className="border p-2">₹1180/year</td>
                          <td className="border p-2">₹150–₹200 per paid user</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-6">
                    <h4 className="font-semibold text-lg mb-2">Payout Conditions:</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
                      <li>Commissions calculated after successful payment</li>
                      <li>Weekly payouts via UPI, Paytm, or bank transfer</li>
                      <li>30-day attribution window for referrals</li>
                      <li>Simple dashboard to track your earnings</li>
                    </ul>
                  </div>
                </motion.div>
              </div>
              
              <div className="lg:col-span-2">
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-amber-100/50 to-purple-100/50 p-6 rounded-lg shadow-md h-full"
                >
                  <h3 className="text-xl font-bold mb-4">Earnings Calculator</h3>
                  <p className="text-gray-700 mb-6">See how much you could earn as an Edaptica affiliate:</p>
                  
                  <div className="space-y-4">
                    <motion.div variants={itemVariants} className="p-3 bg-white rounded-lg">
                      <p className="font-medium">30 Basic Monthly subscribers</p>
                      <p className="text-lg font-bold text-green-700">₹15 × 30 = ₹450</p>
                    </motion.div>
                    
                    <motion.div variants={itemVariants} className="p-3 bg-white rounded-lg">
                      <p className="font-medium">20 Pro Monthly subscribers</p>
                      <p className="text-lg font-bold text-green-700">₹25 × 20 = ₹500</p>
                    </motion.div>
                    
                    <motion.div variants={itemVariants} className="p-3 bg-white rounded-lg">
                      <p className="font-medium">10 Pro Annual subscribers</p>
                      <p className="text-lg font-bold text-green-700">₹150 × 10 = ₹1,500</p>
                    </motion.div>
                  </div>
                  
                  <motion.div variants={itemVariants} className="mt-6 pt-4 border-t border-amber-200">
                    <p className="text-2xl font-bold text-center">Total: ₹2,450 🔥</p>
                  </motion.div>
                  
                  <motion.div variants={itemVariants} className="mt-6 flex justify-center">
                    <Button asChild size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white">
                      <Link to="/affiliate/apply">Start Earning Now</Link>
                    </Button>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </TabsContent>
          
          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-8">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-2xl font-bold mb-6 text-center">Frequently Asked Questions</h3>
                
                <div className="space-y-6">
                  <motion.div variants={itemVariants} className="border-b pb-4">
                    <h4 className="font-semibold mb-2">Who can apply to be an affiliate?</h4>
                    <p className="text-gray-600">
                      Anyone who has an audience in education, learning, or student communities can apply. This includes
                      educators, content creators, bloggers, influencers, and student leaders.
                    </p>
                  </motion.div>
                  
                  <motion.div variants={itemVariants} className="border-b pb-4">
                    <h4 className="font-semibold mb-2">What happens after I submit my application?</h4>
                    <p className="text-gray-600">
                      Our team reviews your application within 2-3 business days. If approved, you'll receive
                      an email with your unique affiliate code and dashboard access.
                    </p>
                  </motion.div>
                  
                  <motion.div variants={itemVariants} className="border-b pb-4">
                    <h4 className="font-semibold mb-2">How do I track my referrals and earnings?</h4>
                    <p className="text-gray-600">
                      You'll get access to a dedicated affiliate dashboard where you can track clicks, conversions,
                      and earnings in real-time.
                    </p>
                  </motion.div>
                  
                  <motion.div variants={itemVariants} className="border-b pb-4">
                    <h4 className="font-semibold mb-2">When and how do I get paid?</h4>
                    <p className="text-gray-600">
                      Payments are processed weekly for all accumulated commissions. You can choose to receive
                      payments via UPI, Paytm, or bank transfer.
                    </p>
                  </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <h4 className="font-semibold mb-2">What promotional materials do you provide?</h4>
                    <p className="text-gray-600">
                      We provide banners, social media templates, email copy, and promotional content to help
                      you effectively promote Edaptica to your audience.
                    </p>
                  </motion.div>
                </div>
              </div>
              
              <div className="text-center mt-6">
                <p className="mb-4 text-gray-600">
                  Have more questions about our affiliate program?
                </p>
                <Button asChild variant="outline">
                  <Link to="/contact-us">Contact Our Affiliate Team</Link>
                </Button>
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
        
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="mt-10 text-center"
        >
          <Link to="/affiliate/terms" className="text-primary hover:text-primary-dark font-medium inline-flex items-center">
            Learn more about our Affiliate Program terms and conditions
            <ChevronDown className="ml-1 h-4 w-4 rotate-270" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
